import React from "react";
import { Box, IconButton, Typography, Button, Fade } from "@mui/material";
import { X, HelpCircle } from "lucide-react";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import CloudIcon from "@mui/icons-material/Cloud";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import AcUnitIcon from "@mui/icons-material/AcUnit";

export default function WeatherWizHowTo({
  open,
  onClose,
  weatherType,
  isEmbedded = false,
}) {
  if (!open) return null;

  const getWeatherIcon = () => {
    switch (weatherType?.label) {
      case "sun":
        return <WbSunnyIcon sx={{ color: "#FFB300", fontSize: "2rem" }} />;
      case "cloud":
        return <CloudIcon sx={{ color: "#78909C", fontSize: "2rem" }} />;
      case "rain":
        return <ThunderstormIcon sx={{ color: "#546E7A", fontSize: "2rem" }} />;
      case "snow":
        return <AcUnitIcon sx={{ color: "#90CAF9", fontSize: "2rem" }} />;
      default:
        return <CloudIcon sx={{ color: "#78909C", fontSize: "2rem" }} />;
    }
  };

  return (
    <>
      <Fade in={open} timeout={300}>
        <Box
          onClick={onClose}
          sx={{
            position: isEmbedded ? "fixed" : "absolute",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 999,
          }}
        />
      </Fade>

      <Fade in={open} timeout={300}>
        <Box
          sx={{
            position: isEmbedded ? "fixed" : "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "400px",
            bgcolor: "white",
            borderRadius: "12px",
            p: 3,
            outline: "none",
            textAlign: "center",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            zIndex: 1000,
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <X size={24} />
          </IconButton>

          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                fontSize: "2.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <HelpCircle size={40} strokeWidth={2.5} />
              How to play
            </Typography>
          </Box>

          {/* Main instruction */}
          <Typography sx={{ mb: 4, fontSize: "1.1rem" }}>
            Fill the grid with {weatherType?.label || "weather"} symbols based
            on the numbers shown.
          </Typography>

          {/* Rules */}
          <Typography
            variant="h6"
            sx={{ textAlign: "left", mb: 2, fontWeight: 600 }}
          >
            Rules:
          </Typography>

          <Box sx={{ textAlign: "left", mb: 4 }}>
            <Typography component="div" sx={{ mb: 1 }}>
              • Numbers on the left show how many {weatherType?.label} symbols
              each row should have
            </Typography>
            <Typography component="div" sx={{ mb: 1 }}>
              • Numbers on the top show how many {weatherType?.label} symbols
              each column should have
            </Typography>
            <Typography component="div">
              • No more than 2 {weatherType?.label} symbols can appear
              consecutively
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={onClose}
            fullWidth
            sx={{
              bgcolor: "black",
              color: "white",
              py: 1.5,
              fontSize: "1.2rem",
              fontWeight: "bold",
              borderRadius: 2,
              "&:hover": {
                bgcolor: "#333",
              },
            }}
          >
            Got it!
          </Button>
        </Box>
      </Fade>
    </>
  );
}
